import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import type { BatchPanelMode } from "./BatchUploadPanel.model";
import type { BatchSummary } from "./batchUpload.types";

/**
 * Souhrn doběhnuté dávky: počty hotových, existujících a chybných souborů, „Zkusit znovu vše
 * chybné“ (jen při opakovatelné chybě, `canRetryAll`) a tlačítko „Hotovo“, kterým začíná
 * nová dávka (na stránce Média zavře oblast, ve výběru fotek vrátí do knihovny).
 */
export default function BatchSummaryBar({
  summary,
  canRetryAll,
  mode,
  onRetryAll,
  onDone,
}: {
  summary: BatchSummary | null;
  canRetryAll: boolean;
  mode: BatchPanelMode;
  onRetryAll: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-3 dark:border-gray-700">
      {summary !== null && (
        <div
          data-batch-summary=""
          data-done={summary.done}
          data-existing={summary.existing}
          data-errors={summary.errors}
          aria-live="polite"
          className="flex-1 text-sm text-gray-700 dark:text-gray-300"
        >
          {[
            t("mediaUpload.summary.done", { count: summary.done }),
            t("mediaUpload.summary.existing", { count: summary.existing }),
            t("mediaUpload.summary.errors", { count: summary.errors }),
          ].join(" · ")}
        </div>
      )}
      <div className="ml-auto flex items-center gap-2">
        {canRetryAll && (
          <button
            type="button"
            data-batch-retry-all=""
            onClick={onRetryAll}
            className="btn-secondary btn-sm"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {t("mediaUpload.retryAll")}
          </button>
        )}
        <button type="button" data-batch-done="" onClick={onDone} className="btn-primary btn-sm">
          {mode === "picker" ? t("shared.mediaPicker.doneBackToLibrary") : t("mediaUpload.finish")}
        </button>
      </div>
    </div>
  );
}
